/**
 * This is a shorted and modified version of express-useragent.js v1.0.7 (https://github.com/biggora/express-useragent)
 * Updates Done:
 *     - Changed from ES5 to ES6 Version.
 *     - Bot, Silk, KindleFire and CaptiveNetwork, NginxGeoIP user agent check methods where removed.
 *     - Fixed lint errors.
 */
class UserAgentParser {

    /* eslint-disable no-useless-escape */
    constructor() {
        this.version = '1.0.4';
        this._Versions = {
            Chrome: /chrome\/([\d\w\.\-]+)/i,
            Chromium: /(?:chromium|crios)\/([\d\w\.\-]+)/i,
            Edge: /Edge\/([\d\w\.\-]+)/i,
            Firefox: /firefox\/([\d\w\.\-]+)/i,
            IE: /msie\s([\d\.]+[\d])|trident\/\d+\.\d+;.*[rv:]+(\d+\.\d)/i,
            Opera: /version\/([\d\w\.\-]+)|OPR\/([\d\w\.\-]+)/i,
            Safari: /version\/([\d\w\.\-]+)/i
        };

        this._Browsers = {
            Chrome: /chrome/i,
            Chromium: /chromium|crios/i,
            Edge: /edge/i,
            Firefox: /firefox/i,
            IE: /msie|trident/i,
            Opera: /opera|OPR/i,
            Safari: /safari/i
        };

        this._Platform = {
            Windows: /windows nt/i,
            WindowsPhone: /windows phone/i,
            Mac: /macintosh/i,
            Linux: /linux/i,
            iPad: /ipad/i,
            iPhone: /iphone/i,
            Android: /android/i,
            Blackberry: /blackberry/i,
            Samsung: /samsung/i
        };

        this.DefaultAgent = {
            isAuthoritative: true,
            isMobile: false,
            isTablet: false,
            isiPad: false,
            isiPhone: false,
            isAndroid: false,
            isBlackberry: false,
            isOpera: false,
            isIE: false,
            isEdge: false,
            isIECompatibilityMode: false,
            isSafari: false,
            isFirefox: false,
            isWebkit: false,
            isChrome: false,
            isDesktop: false,
            isWindows: false,
            isLinux: false,
            isLinux64: false,
            isMac: false,
            isChromeOS: false,
            isSamsung: false,
            isRaspberry: false,
            isAndroidTablet: false,
            browser: 'unknown',
            version: 'unknown',
            os: 'unknown',
            platform: 'unknown',
            source: ''
        };

        this.Agent = this.DefaultAgent;
    }

    /**
     * Get the Browser.
     * @param  {String} string -The user agent string.
     * @returns {String} -The Browser.
     */
    getBrowser = (string) => {
        switch (true) {
            case this._Browsers.Edge.test(string):
                this.Agent.isEdge = true;
                return 'Edge';
            case this._Browsers.Opera.test(string):
                this.Agent.isOpera = true;
                return 'Opera';
            case this._Browsers.Chromium.test(string):
                this.Agent.isChrome = true;
                return 'Chromium';
            case this._Browsers.Chrome.test(string):
                this.Agent.isChrome = true;
                return 'Chrome';
            case this._Browsers.IE.test(string):
                this.Agent.isIE = true;
                return 'IE';
            case this._Browsers.Safari.test(string):
                this.Agent.isSafari = true;
                return 'Safari';
            case this._Browsers.Firefox.test(string):
                this.Agent.isFirefox = true;
                return 'Firefox';
            default:
                // If the UA does not start with Mozilla guess the user agent.
                if (string.indexOf('Mozilla') !== 0 && /^([\d\w\-\.]+)\/[\d\w\.\-]+/i.test(string)) {
                    this.Agent.isAuthoritative = false;
                    return RegExp.$1;
                }
                return 'unknown';
        }
    };

    /**
     * Get the BrowserVersion.
     * @param  {String} string - The user agent string.
     * @returns {String} - The Browser Version.
     */
    getBrowserVersion = (string) => {
        let regex;
        let version = '';
        switch (this.Agent.browser) {
            case 'Edge':
                if (this._Versions.Edge.test(string)) {
                    version = RegExp.$1;
                }
                break;
            case 'Chrome':
                if (this._Versions.Chrome.test(string)) {
                    version = RegExp.$1;
                }
                break;
            case 'Chromium':
                if (this._Versions.Chromium.test(string)) {
                    version = RegExp.$1;
                }
                break;
            case 'Safari':
                if (this._Versions.Safari.test(string)) {
                    version = RegExp.$1;
                }
                break;
            case 'Opera':
                if (this._Versions.Opera.test(string)) {
                    version = RegExp.$1 ? RegExp.$1 : RegExp.$2;
                }
                break;
            case 'Firefox':
                if (this._Versions.Firefox.test(string)) {
                    version = RegExp.$1;
                }
                break;
            case 'IE':
                if (this._Versions.IE.test(string)) {
                    version = RegExp.$2 ? RegExp.$2 : RegExp.$1;
                }
                break;
            default:
                if (this.Agent.browser !== 'unknown') {
                    // eslint-disable-next-line security/detect-non-literal-regexp
                    regex = new RegExp(`${this.Agent.browser}[\\/ ]([\\d\\w\\.\\-]+)`, 'i');
                    if (regex.test(string)) {
                        version = RegExp.$1;
                    }
                }
        }
        return version;
    };

    /**
     * get the Platform
     * @param  {String} string - The user agent string.
     * @returns {String} - The Platform.
     */
    getPlatform = (string) => {
        switch (true) {
            case this._Platform.Windows.test(string):
                return 'Microsoft Windows';
            case this._Platform.WindowsPhone.test(string):
                this.Agent.isWindowsPhone = true;
                return 'Microsoft Windows Phone';
            case this._Platform.Mac.test(string):
                return 'Apple Mac';
            case this._Platform.Android.test(string):
                this.Agent.isAndroid = true;
                return 'Android';
            case this._Platform.Blackberry.test(string):
                this.Agent.isBlackberry = true;
                return 'Blackberry';
            case this._Platform.iPad.test(string):
                this.Agent.isiPad = true;
                return 'iPad';
            case this._Platform.iPhone.test(string):
                this.Agent.isiPhone = true;
                return 'iPhone';
            case this._Platform.Samsung.test(string):
                this.Agent.isiSamsung = true;
                return 'Samsung';
            default:
                return 'unknown';
        }
    };

    /**
     * Test the compatibility mode
     */
    testCompatibilityMode = () => {
        const ua = this;
        if (this.Agent.isIE) {
            if (/Trident\/(\d)\.0/i.test(ua.Agent.source)) {
                const tridentVersion = parseInt(RegExp.$1, 10);
                const version = parseInt(ua.Agent.version, 10);
                if (version === 7 && tridentVersion === 7) {
                    ua.Agent.isIECompatibilityMode = true;
                    ua.Agent.version = 11.0;
                }

                if (version === 7 && tridentVersion === 6) {
                    ua.Agent.isIECompatibilityMode = true;
                    ua.Agent.version = 10.0;
                }

                if (version === 7 && tridentVersion === 5) {
                    ua.Agent.isIECompatibilityMode = true;
                    ua.Agent.version = 9.0;
                }

                if (version === 7 && tridentVersion === 4) {
                    ua.Agent.isIECompatibilityMode = true;
                    ua.Agent.version = 8.0;
                }
            }
        }
    };

    /**
     * Reset the parser.
     * @returns {Object} - The user agent.
     */
    reset = () => {
        const ua = this;
        for (const key in ua.DefaultAgent) {
            if (ua.DefaultAgent.hasOwnProperty(key)) {
                ua.Agent[key] = ua.DefaultAgent[key];
            }
        }
        return ua;
    };

    /**
     * Test if is a Mobile.
     */
    testMobile = () => {
        const ua = this;
        switch (true) {
            case ua.Agent.isWindows:
            case ua.Agent.isLinux:
            case ua.Agent.isMac:
            case ua.Agent.isChromeOS:
                ua.Agent.isDesktop = true;
                break;
            case ua.Agent.isAndroid:
            case ua.Agent.isSamsung:
                ua.Agent.isMobile = true;
                ua.Agent.isDesktop = false;
                break;
            default:
        }
        switch (true) {
            case ua.Agent.isiPad:
            case ua.Agent.isiPhone:
            case ua.Agent.isBlackberry:
            case ua.Agent.isAndroid:
            case ua.Agent.isWindowsPhone:
                ua.Agent.isMobile = true;
                ua.Agent.isDesktop = false;
                break;
            default:
        }
        if (/mobile/i.test(ua.Agent.source)) {
            ua.Agent.isMobile = true;
            ua.Agent.isDesktop = false;
        }
    };

    /**
     * Test if is a Tablet.
     */
    testTablet = () => {
        const ua = this;
        switch (true) {
            case ua.Agent.isiPad:
            case ua.Agent.isAndroidTablet:
                ua.Agent.isTablet = true;
                break;
            default:
        }
        if (/tablet/i.test(ua.Agent.source)) {
            ua.Agent.isTablet = true;
        }
    };

    /**
     * Test if is an Android Tablet.
     */
    testAndroidTablet = () => {
        const ua = this;
        if (ua.Agent.isAndroid && !/mobile/i.test(ua.Agent.source)) {
            ua.Agent.isAndroidTablet = true;
        }
    };

    /**
     * Check the Browser version.
     * @param  {String} regexString - The user agent string.
     * @returns {Boolean} - True if the browser Version matches the regex.
     */
    checkBrowserVersionMask = (regexString) => {
        const ua = this;
        // eslint-disable-next-line security/detect-non-literal-regexp
        const versionRegExp = new RegExp(regexString, 'i');
        const version = this.getBrowserVersion(ua.Agent.source);
        return versionRegExp.test(version);
    };

    /* eslint-enable no-useless-escape */

}

export { UserAgentParser };
